import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
  UploadTaskSnapshot,
} from 'firebase/storage';
import imageCompression from 'browser-image-compression';

export interface UploadedRequestPhotoAsset {
  assetId: string;
  url: string;
  storagePath: string;
  contentType: string;
  bytes: number;
}

const REQUEST_PHOTO_MAX_SIZE_MB = 1;

const sanitizeFileName = (fileName: string): string =>
  fileName
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'photo.jpg';

const createAssetId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const uploadSingleRequestPhoto = async (
  requestId: string,
  file: File
): Promise<UploadedRequestPhotoAsset> => {
  const compressedFile = await imageCompression(file, {
    maxSizeMB: REQUEST_PHOTO_MAX_SIZE_MB,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    initialQuality: 0.7,
  });
  const assetId = createAssetId();
  const storagePath = `repair-request-photos/${requestId}/${assetId}-${sanitizeFileName(file.name)}`;
  const storageRef = ref(getStorage(), storagePath);

  await new Promise<void>((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, compressedFile, {
      contentType: compressedFile.type || file.type || 'image/jpeg',
    });
    uploadTask.on(
      'state_changed',
      (_snapshot: UploadTaskSnapshot) => undefined,
      reject,
      () => resolve()
    );
  });

  const url = await getDownloadURL(storageRef);
  return {
    assetId,
    url,
    storagePath,
    contentType: compressedFile.type || file.type || 'image/jpeg',
    bytes: compressedFile.size,
  };
};

export const uploadRequestPhotos = async (
  requestId: string,
  files: File[]
): Promise<UploadedRequestPhotoAsset[]> => {
  const uniqueFiles = files.slice(0, 5);
  const uploaded: UploadedRequestPhotoAsset[] = [];
  for (const file of uniqueFiles) {
    uploaded.push(await uploadSingleRequestPhoto(requestId, file));
  }
  return uploaded;
};
